import { atom } from 'jotai';
import { ChannelType, PaginatedResponseType } from '../../globals/types';
import { IBrandPricing } from '../pricing';

export interface IInvoice {
    id?: number;
    invoice_name: string;
    created_at: string;
    brand_id: number;
    total_cost: number;
    order_id: number;
    charge_status: 'SUCCEEDED' | 'REFUNDED' | 'PENDING' | 'FAILED';
    invoice_line_items: IInvoiceLineItem[];
}

export interface IInvoiceLineItem {
    id: number;
    created_at: string;
    quantity: number;
    cost: number;
    unit_cost: number;
    /**
     * NULL for synthesized charges that have no `pricings` row behind them —
     * packaging, shipping, influencer discounts and artist royalties. Only lines
     * priced from a brand's rate card carry a pricing.
     */
    pricing: IBrandPricing | null;
    /**
     * Label written at charge time. Preferred over the pricing_source display
     * name, and the ONLY label available for synthesized charges. NULL on rows
     * created before the column existed.
     */
    description?: string | null;
}

export interface GetInvoicesBody {
    brand_id: number | null; // null = all brands (super users); RPC treats NULL as unscoped
    page_size: number;
    offset_pages: number;
    querystring: string | null;
    start_date?: string | null;
    end_date?: string | null;
    charge_statuses?: string[] | null;
}

export type InvoiceChargeStatus = 'SUCCEEDED' | 'REFUNDED' | 'PENDING' | 'FAILED';

export enum InvoicesQuickRange {
    LAST_7_DAYS = 'Last 7 days',
    LAST_MONTH = 'Last 1 month',
    THIS_YEAR = 'This year',
    LAST_YEAR = 'Last year',
}

export interface InvoicesFilter {
    query_string?: string;
    charge_statuses?: InvoiceChargeStatus[];
    start_date?: string;
    end_date?: string;
    quick_range?: InvoicesQuickRange;
    // Super-user brand switch: undefined = own brand (default), null = all brands, number = that brand.
    brand_id?: number | null;
}

export const invoicesFilterAtom = atom<InvoicesFilter>({});

export const invoicesAtom = atom<PaginatedResponseType<IInvoice>>({ results: [], count: 0, hasNext: false, hasBefore: false, channel: ChannelType.WALLMATES });
export const invoicesLoadingAtom = atom<boolean>(false);
export const singleInvoiceAtom = atom<IInvoice | null>(null);

// Infinite scroll atoms
export const invoicesAccumulatedAtom = atom<IInvoice[]>([]);
export const invoicesHasMoreAtom = atom<boolean>(true);
export const invoicesCurrentPageAtom = atom<number>(0);
export const invoicesLoadingMoreAtom = atom<boolean>(false);
export const invoicesCountAtom = atom<number>(0);

// Statement download state
export const invoicesDownloadingStatementAtom = atom<boolean>(false);

// Scroll position & highlight atoms
export const invoiceListScrollPositionAtom = atom<number>(0);
export const lastViewedInvoiceIdAtom = atom<number | null>(null);
export const navigatedFromInvoiceDetailAtom = atom<boolean>(false);
